import React from 'react';
import { Container, Typography, Grid, Box, Card, CardContent, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Main from 'layouts/Main';
import SEO from '../../../components/SEO';
import StructuredData from '../../../components/StructuredData';
import BreadcrumbsComponent from '../../../components/Breadcrumbs';
import RelatedContent from '../../../components/RelatedContent';
import contentRegistry from '../../../config/contentRegistry';
import { TrendingUp as TrendingUpIcon } from '@mui/icons-material';

const StrategiesHub = () => {
  const theme = useTheme();
  const guides = contentRegistry.guides.strategies;

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Learn', path: '/learn' },
    { label: 'Strategies', path: '/learn/strategies' }
  ];

  return (
    <Main>
      <SEO
        title="Advanced Betting Strategies: Bankroll Management & Analytics | +EV Bets"
        description="Master advanced betting strategies including bankroll management, Kelly Criterion, and data-driven analytics for experienced bettors."
        keywords="betting strategies, bankroll management, Kelly Criterion, betting analytics, advanced betting, sports betting strategies"
        og={{
          title: 'Advanced Betting Strategies: Bankroll Management & Analytics | +EV Bets',
          description: 'Master advanced betting strategies including bankroll management and analytics.',
          image: 'https://plusevbets.com/og-image.png',
          url: 'https://plusevbets.com/learn/strategies'
        }}
      />
      <StructuredData
        type="ItemList"
        data={{
          name: 'Advanced Betting Strategies',
          description: 'Advanced strategies for experienced bettors including bankroll management and analytics',
          url: 'https://plusevbets.com/learn/strategies',
          numberOfItems: guides.length,
          itemListElement: guides.map((guide, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'Article',
              name: guide.title,
              url: `https://plusevbets.com${guide.path}`
            }
          }))
        }}
      />

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          position: 'relative',
          overflow: 'hidden',
          py: { xs: 6, md: 8 },
          mb: { xs: 4, md: 6 }
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', maxWidth: '700px', mx: 'auto' }}>
            <TrendingUpIcon sx={{ fontSize: 60, color: 'white', mb: 2 }} />
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                fontWeight: 700,
                color: 'white',
                mb: 2,
                lineHeight: 1.2
              }}
            >
              Advanced Strategies
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 400,
                lineHeight: 1.6
              }}
            >
              Take your betting to the next level with advanced strategies, bankroll management, and data-driven analytics.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          {/* Breadcrumbs */}
          <BreadcrumbsComponent items={breadcrumbItems} />

          {/* Guides Grid */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            {guides.map((guide) => (
              <Grid item xs={12} sm={6} md={4} key={guide.path}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3 }}>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                      {guide.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 3,
                        flexGrow: 1,
                        color: 'text.secondary',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {guide.description}
                    </Typography>
                    <Button
                      component={Link}
                      to={guide.path}
                      variant="contained"
                      color="primary"
                      fullWidth
                    >
                      Read Guide
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Related Content */}
          <RelatedContent
            category="quick-start"
            title="New to EV Betting? Start Here"
            limit={4}
            currentPath="/learn/strategies"
          />
        </Box>
      </Container>
    </Main>
  );
};

export default StrategiesHub;
